import { Component, Input, OnInit } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { DataPointsResult } from '../../../interfaces/data-points';
import { DataPointsService, ReportPeriod, TimeSerie, TimeSeriesDetails } from '../../../services/data-points.service';
import { SeriesOptionsType } from 'highcharts';
import { TranslateService } from '@ngx-translate/core';
import { getDatapoint } from '../../../utils/mixit-utils';
import { first, switchMap, map, shareReplay } from 'rxjs/operators';
import { convertCubicMeterPerSecondToCubicMeterPerHour } from '../../../utils/data-point-utils';
import { BoosterDataPoints } from '../../../interfaces/booster';
import { UserTrackingPeriodOptions } from '../../../services/user-tracking-helper.service';
import { ChartInterface } from '../chart.interface';
import { SystemDeviceType } from '../../../interfaces/systemDeviceType';
import { AsyncPipe } from '@angular/common';
import { ChartTileComponent } from '../chart-tile/chart-tile.component';

@Component({
    selector: 'app-booster-flow-pressure-chart[installationId][applicationId]',
    templateUrl: './booster-flow-pressure-chart.component.html',
    styleUrls: ['./booster-flow-pressure-chart.component.scss'],
    imports: [
    ChartTileComponent,
    AsyncPipe
]
})
export class BoosterFlowPressureChartComponent implements OnInit, ChartInterface {
  @Input() dataPoints$: Observable<DataPointsResult>;
  @Input() title: string;
  @Input() userTrackingPeriodOptions: UserTrackingPeriodOptions;
  @Input() applicationId: string;
  @Input() installationId: string;

  public getSeries: (period: ReportPeriod) => Observable<SeriesOptionsType[]>;
  public dataPointsForChart$: Observable<any>;
  public legends$ = combineLatest([
    this.translateService.get('booster-flow-pressure-chart.flow-legend'),
    this.translateService.get('booster-flow-pressure-chart.head-bar'),
    this.translateService.get('booster-flow-pressure-chart.setpoint-bar'),
  ]);
  public installationId$: Observable<string>;
  public options: {
    colors: ['#CCC82D', '#009EE3'];
    yAxis: [
      {
        title: {
          text: null;
        };
        labels: {
          format: '{value} m3/h';
        };
        min: 0;
      },
      {
        title: {
          text: null;
        };
        labels: {
          format: '{value} Bar';
        };
        min: 0;
        softMax: 5;
        opposite: true;
      }
    ];
  };

  constructor(private translateService: TranslateService, private dataPointsService: DataPointsService) {}

  ngOnInit(): void {
    const flow$ = getDatapoint(this.dataPoints$, SystemDeviceType.BoosterSystem, BoosterDataPoints.Flow).pipe(first());
    const headDataPoint$ = getDatapoint(this.dataPoints$, SystemDeviceType.BoosterSystem, BoosterDataPoints.SystemPress).pipe(first());
    const setPointDataPoint$ = getDatapoint(this.dataPoints$, SystemDeviceType.BoosterSystem, BoosterDataPoints.SysRef).pipe(first());
    this.dataPointsForChart$ = combineLatest([flow$, headDataPoint$, setPointDataPoint$]);

    this.installationId$ = this.dataPoints$.pipe(
      first(),
      map((d) => d.installationId)
    );

    this.getSeries = (period: ReportPeriod) => {
      return combineLatest([this.dataPointsForChart$, this.legends$]).pipe(
        switchMap(([[supplyDataPoint, headDataPoint, setPointDataPoint], [flowLegend, headLegend, setPointLegend]]) => {
          const timeSeriesDetails: TimeSeriesDetails = {
            dataPoints: [supplyDataPoint, headDataPoint, setPointDataPoint],
            period,
            installationId: this.installationId,
            systemId: this.applicationId,
            requestId: this.title.replace(/\W+/g, '_').toLowerCase(),
          };
          return this.dataPointsService.getTimeSeries(timeSeriesDetails).pipe(
            map((seriesData: TimeSerie[]) => {
              return [
                {
                  name: flowLegend,
                  data: convertCubicMeterPerSecondToCubicMeterPerHour(seriesData[0]),
                  type: 'line',
                  tooltip: { valueSuffix: ' m3/h' },
                } as SeriesOptionsType,
                { name: headLegend, data: seriesData[1], type: 'line', tooltip: { valueSuffix: 'Bar' }, yAxis: 1 } as SeriesOptionsType,
                {
                  name: setPointLegend,
                  data: seriesData[2],
                  type: 'line',
                  tooltip: { valueSuffix: ' Bar' },
                  yAxis: 1,
                } as SeriesOptionsType,
              ];
            })
          );
        }),
        shareReplay(1)
      );
    };
  }
}
